import { Injectable } from '@angular/core'
import type {
  NavigationBehaviorOptions,
  UrlCreationOptions,
  UrlTree,
} from '@angular/router'
import { Router } from '@angular/router'

const LOCALE_PREFIX = '/en'

function isEnglishUrl(url: string): boolean {
  return (
    url === LOCALE_PREFIX ||
    url.startsWith(`${LOCALE_PREFIX}/`) ||
    url.startsWith(`${LOCALE_PREFIX}?`)
  )
}

/**
 * Prefixes an absolute, app-internal path with /en when `currentUrl` is on the
 * English branch. Exported so that code which can't go through the Router at
 * all (raw `<a href>` markup rendered via a translated `[innerHTML]` string)
 * can apply the same rule. See products-checkout/article-purchase-checkout,
 * which interpolate a terms-and-conditions link into translated HTML.
 */
export function withLocalePrefix(currentUrl: string, path: string): string {
  if (!isEnglishUrl(currentUrl) || path.startsWith(LOCALE_PREFIX)) {
    return path
  }

  return path === '/' ? LOCALE_PREFIX : `${LOCALE_PREFIX}${path}`
}

/**
 * Rewrites every absolute navigation (routerLink, router.navigate,
 * router.navigateByUrl, router.createUrlTree) so it keeps the /en prefix while
 * browsing an English page, without every call site having to know about locale
 * prefixes.
 */
@Injectable()
export class LocaleAwareRouter extends Router {
  override createUrlTree(
    commands: readonly unknown[],
    navigationExtras?: UrlCreationOptions
  ): UrlTree {
    return super.createUrlTree(
      this.withLocalePrefixedCommands(commands, navigationExtras),
      navigationExtras
    )
  }

  override navigateByUrl(
    url: string | UrlTree,
    extras?: NavigationBehaviorOptions
  ): Promise<boolean> {
    if (typeof url === 'string') {
      return super.navigateByUrl(
        withLocalePrefix(this.currentUrlForPrefixing(), url),
        extras
      )
    }

    return super.navigateByUrl(url, extras)
  }

  private withLocalePrefixedCommands(
    commands: readonly unknown[],
    navigationExtras?: UrlCreationOptions
  ): readonly unknown[] {
    const [first, ...rest] = commands
    const currentUrl = this.currentUrlForPrefixing()

    if (typeof first === 'string' && first.startsWith('/')) {
      return [withLocalePrefix(currentUrl, first), ...rest]
    }

    // Without an explicit relativeTo, Angular resolves commands as rooted
    // from '/' even when they don't start with a slash - unlike routerLink,
    // which always supplies its own hosting route as relativeTo. Plain
    // router.navigate(['products', 'checkout']) calls hit this branch.
    if (!navigationExtras?.relativeTo && isEnglishUrl(currentUrl)) {
      return ['/en', ...commands]
    }

    return commands
  }

  /**
   * Prefers the in-flight navigation's target over the last-committed `url`.
   * While a redirect guard is running (e.g. oldArticleIdRedirectGuard on a
   * fresh request to /en?oldid=...), `this.url` still reflects the previous
   * page, which would otherwise silently drop the /en prefix from the redirect
   * target (or add one that shouldn't be there while leaving /en).
   */
  private currentUrlForPrefixing(): string {
    const navigation = this.currentNavigation()
    const targetTree = navigation?.finalUrl ?? navigation?.extractedUrl
    return targetTree ? this.serializeUrl(targetTree) : this.url
  }
}
